/**
 * The room's timer as everyone sees it: minutes and seconds left, mustard in
 * the last thirty seconds, "Time is up" at zero. The end time is worked out
 * from the server's seconds-left (`nextTimerEnd`, lib/sessions/roomRules), so a wrong clock on a
 * phone does not matter. Shared by the live room, the Board, the guest room
 * and the Mental Map room.
 */
import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';

export interface RoomCountdownProps {
  /** When the timer reaches zero, in this browser's clock (ms). */
  endsAt: number;
  /** Shown instead of "Time is up" at zero, e.g. "Time is up · answers are locked". */
  atZeroLabel?: string;
  className?: string;
}

export function RoomCountdown({ endsAt, atZeroLabel, className = '' }: RoomCountdownProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.ceil((endsAt - now) / 1000));
  const up = left === 0;
  return (
    <span
      role="timer"
      data-testid="room-countdown"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-sm ${
        up ? 'border-[var(--coral)] text-[var(--coral)]' : left <= 30 ? 'border-[var(--mustard)] text-[var(--text-pri)]' : 'border-[var(--rule-bright)] text-[var(--text-pri)]'
      } ${className}`}
    >
      <Timer className="h-4 w-4" />
      {up ? atZeroLabel ?? 'Time is up' : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} left`}
    </span>
  );
}
